import { FOOTER_PREMIUM } from "../common/footer";
import { ONBOARDING_HEADER } from "../common/header";

/**
 * World-Class Onboarding Day 1 Template
 * Features: Welcome message, progress tracking, step-by-step guide, social proof
 */
export const onboardingDay1 = `
<mjml>
  <mj-head>
    <mj-title>Welcome to {{product_name}} - Let's Get Started!</mj-title>
    <mj-preview>Your journey begins here - let's set up your first chatbot</mj-preview>
    <mj-attributes>
      <mj-all font-family="'Segoe UI', Arial, sans-serif" />
      <mj-text font-size="16px" color="#334155" line-height="1.6" />
      <mj-button font-family="'Segoe UI', Arial, sans-serif" font-size="16px" font-weight="600" />
    </mj-attributes>
    <mj-style>
      .welcome-badge { background: linear-gradient(135deg, #10B981, #059669); color: white; padding: 6px 16px; border-radius: 20px; font-size: 12px; font-weight: 700; }
      .step-circle { background: linear-gradient(135deg, #0EA5E9, #0284C7); color: white; width: 48px; height: 48px; border-radius: 50%; display: inline-block; line-height: 48px; font-weight: 700; font-size: 20px; text-align: center; }
    </mj-style>
  </mj-head>
  <mj-body background-color="#f0f9ff">
    \${ONBOARDING_HEADER("Welcome Aboard!", "Day 1 of your journey")}

    <!-- Welcome Banner -->
    <mj-section background-color="#ffffff" padding="35px 30px" border-radius="0 0 16px 16px">
      <mj-column>
        <mj-text align="center" padding-bottom="20px">
          <span style="background: linear-gradient(135deg, #10B981, #059669); color: white; padding: 8px 20px; border-radius: 25px; font-size: 13px; font-weight: 700;">&#127881; Email 1 of 5</span>
        </mj-text>
        <mj-text font-size="28px" color="#0f172a" font-weight="700" align="center" padding-bottom="12px">
          Welcome to {{product_name}}, {{first_name}}!
        </mj-text>
        <mj-text font-size="15px" color="#64748b" align="center">
          We're thrilled to have you on board. Over the next few days, we'll guide you through everything you need to get the most out of your chatbots.
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Progress Bar -->
    <mj-section background-color="#ffffff" padding="25px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="13px" color="#64748b" padding-bottom="12px">
          Your onboarding progress:
        </mj-text>
        <mj-raw>
          <div style="background-color: #e2e8f0; border-radius: 10px; height: 12px; overflow: hidden;">
            <div style="background: linear-gradient(90deg, #0EA5E9, #2DD4BF); height: 100%; width: 20%; border-radius: 10px;"></div>
          </div>
        </mj-raw>
        <mj-text font-size="13px" color="#0EA5E9" font-weight="600" padding-top="8px">
          20% Complete
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Today's Goal -->
    <mj-section background-color="#f0f9ff" padding="30px" border-radius="16px" border="2px solid #bae6fd">
      <mj-column>
        <mj-text font-size="12px" color="#0EA5E9" font-weight="700" text-transform="uppercase" letter-spacing="1px" padding-bottom="8px">
          Today's Goal
        </mj-text>
        <mj-text font-size="22px" color="#0f172a" font-weight="700" padding-bottom="12px">
          Create Your First Chatbot &#128640;
        </mj-text>
        <mj-text font-size="15px" color="#64748b">
          Let's start with the basics. Creating your first chatbot takes just 5 minutes and will give you a great foundation to build on.
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- 3 Steps Section -->
    <mj-section background-color="#ffffff" padding="30px 30px 20px" border-radius="16px 16px 0 0">
      <mj-column>
        <mj-text font-size="20px" color="#0f172a" font-weight="700" padding-bottom="5px">
          3 Quick Steps to Get Started
        </mj-text>
      </mj-column>
    </mj-section>

    <mj-section background-color="#ffffff" padding="0 30px 20px">
      <mj-column padding="15px" background-color="#f8fafc" border-radius="12px">
        <mj-text>
          <table style="width: 100%;">
            <tr>
              <td style="width: 60px; vertical-align: top;">
                <span style="background: linear-gradient(135deg, #0EA5E9, #0284C7); color: white; width: 44px; height: 44px; border-radius: 50%; display: inline-block; line-height: 44px; font-weight: 700; font-size: 18px; text-align: center;">1</span>
              </td>
              <td style="vertical-align: top; padding-left: 10px;">
                <strong style="font-size: 17px; color: #0f172a;">Choose a Template</strong><br>
                <span style="color: #64748b; font-size: 14px;">Browse our template library and pick one that matches your use case - customer support, lead generation, or FAQ bot.</span>
              </td>
            </tr>
          </table>
        </mj-text>
      </mj-column>
    </mj-section>

    <mj-section background-color="#ffffff" padding="0 30px 20px">
      <mj-column padding="15px" background-color="#f8fafc" border-radius="12px">
        <mj-text>
          <table style="width: 100%;">
            <tr>
              <td style="width: 60px; vertical-align: top;">
                <span style="background: linear-gradient(135deg, #0EA5E9, #0284C7); color: white; width: 44px; height: 44px; border-radius: 50%; display: inline-block; line-height: 44px; font-weight: 700; font-size: 18px; text-align: center;">2</span>
              </td>
              <td style="vertical-align: top; padding-left: 10px;">
                <strong style="font-size: 17px; color: #0f172a;">Customize Your Bot</strong><br>
                <span style="color: #64748b; font-size: 14px;">Add your brand colors, logo, and personalize the conversation flows to match your business needs.</span>
              </td>
            </tr>
          </table>
        </mj-text>
      </mj-column>
    </mj-section>

    <mj-section background-color="#ffffff" padding="0 30px 30px" border-radius="0 0 16px 16px">
      <mj-column padding="15px" background-color="#f8fafc" border-radius="12px">
        <mj-text>
          <table style="width: 100%;">
            <tr>
              <td style="width: 60px; vertical-align: top;">
                <span style="background: linear-gradient(135deg, #0EA5E9, #0284C7); color: white; width: 44px; height: 44px; border-radius: 50%; display: inline-block; line-height: 44px; font-weight: 700; font-size: 18px; text-align: center;">3</span>
              </td>
              <td style="vertical-align: top; padding-left: 10px;">
                <strong style="font-size: 17px; color: #0f172a;">Deploy to Your Website</strong><br>
                <span style="color: #64748b; font-size: 14px;">Copy the embed code and paste it into your website. Your chatbot will be live in seconds!</span>
              </td>
            </tr>
          </table>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- CTA Section -->
    <mj-section background-color="#0EA5E9" padding="35px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="24px" color="#ffffff" font-weight="700" align="center" padding-bottom="10px">
          Ready to Create Your First Chatbot?
        </mj-text>
        <mj-text font-size="15px" color="rgba(255,255,255,0.9)" align="center" padding-bottom="20px">
          It only takes 5 minutes to get started.
        </mj-text>
        <mj-button background-color="#ffffff" color="#0EA5E9" href="{{dashboard_url}}" border-radius="8px" padding="18px 45px" font-size="17px" font-weight="700">
          Create My Chatbot &#8594;
        </mj-button>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- What's Coming Section -->
    <mj-section background-color="#ffffff" padding="30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="18px" color="#0f172a" font-weight="700" padding-bottom="15px">
          What's Coming Next?
        </mj-text>
        <mj-text font-size="14px" color="#64748b" line-height="2">
          <strong style="color: #0f172a;">Tomorrow (Day 2):</strong> Designing engaging conversation flows<br/>
          <strong style="color: #0f172a;">Day 3:</strong> Adding AI capabilities to your bot<br/>
          <strong style="color: #0f172a;">Day 4:</strong> Integrating with your favorite tools<br/>
          <strong style="color: #0f172a;">Day 5:</strong> Advanced tips and best practices
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="15px 0"></mj-section>

    <!-- Help Section -->
    <mj-section background-color="#F8FAFC" padding="25px 30px" border-radius="16px">
      <mj-column>
        <mj-text font-size="15px" color="#64748b" align="center">
          Need help? Reply to this email or check out our <a href="{{help_center_url}}" style="color: #0EA5E9; text-decoration: none; font-weight: 600;">Help Center</a>
        </mj-text>
      </mj-column>
    </mj-section>

    <!-- Spacer -->
    <mj-section padding="10px 0"></mj-section>

    \${FOOTER_PREMIUM}
  </mj-body>
</mjml>
`;

export default {
  id: "onboardingDay1",
  name: "Onboarding - Day 1 Welcome",
  category: "sequences",
  purpose: "sequence" as const,
  description:
    "A world-class Day 1 onboarding email with gradient progress bar, numbered step cards, today's goal highlight, and preview of upcoming emails.",
  features: [
    "Welcome badge with email count",
    "Visual progress bar",
    "Today's goal highlight card",
    "Numbered step-by-step cards",
    "Preview of upcoming emails",
    "Premium footer with support",
  ],
  subject: "Welcome to {{product_name}} - Let's Get Started! &#127881;",
  subTitle: "Your journey begins here",
  mjmlTemplate: onboardingDay1,
  tags: ["sequence", "onboarding", "welcome", "day1", "getting-started"],
};
